/**
 * Role registry — the SolarOS actors and their access, in one place.
 *
 * Each role maps to a human label, the slug of the page a member of that role
 * should land on after login (`landingSlug`), and the set of page slugs the role
 * may reach (`allowedSlugs`, or `'*'` for full access). RoleRoute reads this to
 * gate role-scoped routes, and the login flow reads `landingSlug` to route each
 * actor to a page it can view (never an admin-gated landing).
 *
 * Backend `role` claims (JWT) use these same codes. `user` is the public
 * "Any User" actor; `admin` is the platform superuser (seed-only, full access).
 * The codes mirror backend/src/modules/auth/auth.validation.js SELF_REGISTERABLE_ROLES.
 */

// Page slugs every authenticated actor may always reach (public/landing pages).
const COMMON_SLUGS = ['home'];

export const ROLES = {
  admin: {
    code: 'admin',
    label: 'Administrator',
    landingSlug: 'administration',
    allowedSlugs: '*',
    selfRegisterable: false,
  },
  operator: {
    code: 'operator',
    label: 'SolarOS Operator',
    landingSlug: 'administration',
    allowedSlugs: '*',
    selfRegisterable: true,
  },
  epc_installer: {
    code: 'epc_installer',
    label: 'EPC / Installer',
    landingSlug: 'site-registry',
    allowedSlugs: [...COMMON_SLUGS, 'site-registry', 'telemetry-go-live', 'workflow-tracker', 'evidence-assembler'],
    selfRegisterable: true,
  },
  sustainability_lead: {
    code: 'sustainability_lead',
    label: 'Corporate Sustainability Lead',
    landingSlug: 'mrv-emissions',
    allowedSlugs: [...COMMON_SLUGS, 'mrv-emissions', 'verifier-space'],
    selfRegisterable: true,
  },
  prosumer: {
    code: 'prosumer',
    label: 'Prosumer / Corporate',
    landingSlug: 'prosumer-pooling',
    allowedSlugs: [...COMMON_SLUGS, 'prosumer-pooling'],
    selfRegisterable: true,
  },
  utility: {
    code: 'utility',
    label: 'Utility / DU',
    landingSlug: 'utility-rps',
    allowedSlugs: [...COMMON_SLUGS, 'utility-rps'],
    selfRegisterable: true,
  },
  user: {
    code: 'user',
    label: 'Any User',
    landingSlug: 'home',
    allowedSlugs: COMMON_SLUGS,
    selfRegisterable: true,
  },
};

export const DEFAULT_ROLE = 'user';

/** Roles a visitor may pick on the registration form (excludes admin). */
export const REGISTERABLE_ROLES = Object.values(ROLES).filter((r) => r.selfRegisterable);

/** Normalize an unknown/absent role code to a known role definition. */
export function roleDef(code) {
  return ROLES[code] || ROLES[DEFAULT_ROLE];
}

/** The route path an actor should land on after login. */
export function landingPathFor(code) {
  const slug = roleDef(code).landingSlug;
  return slug === 'home' ? '/' : `/${slug}`;
}

/** Whether a role may reach a given page slug. */
export function canAccessSlug(code, slug) {
  const allowed = roleDef(code).allowedSlugs;
  return allowed === '*' || allowed.includes(slug);
}

export default ROLES;
